// Spec #39: forgot-password. Always shows the same success message so
// we don't leak which emails are registered.
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { AuthLayout } from '@/app/AuthLayout';
import { ApiException } from '@/api/errors';

export function ForgotPasswordScreen() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (e) {
      const err = e instanceof ApiException ? e : null;
      setError(
        err && err.statusCode === 0
          ? 'We could not reach the server. Try again in a moment.'
          : 'If an account exists for that email, a reset link is on the way.',
      );
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter the email on your account and we will send you a reset link."
    >
      {sent ? (
        <div className="stack">
          <div className="info-banner">
            If an account exists for <strong>{email}</strong>, a reset link is on the way. Check
            your inbox (and spam folder) for an email from FieldReport AI.
          </div>
          <Link to="/login" className="btn btn--primary">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form className="stack" onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {error ? <div className="error-banner">{error}</div> : null}
          <button className="btn btn--primary" type="submit" disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
